import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Layers, Plus } from 'lucide-react';
import { PatientTabFrame } from '@/components/patient/PatientTabFrame';
import { CourseCount, CourseTabPanel } from '@/components/patient/course/CourseChrome';
import { BookedTreatmentCourseEditor } from '@/components/patient/BookedTreatmentCourseEditor';
import { CancelTreatmentCourseModal } from '@/components/patient/CancelTreatmentCourseModal';
import { ConfirmActionModal } from '@/components/patient/PatientTabActions';
import { TreatmentCourseOverview } from '@/components/patient/TreatmentCourseOverview';
import { TreatmentCourseStepWorkspace } from '@/components/patient/TreatmentCourseStepWorkspace';
import {
  CourseListActiveCard,
  CourseListClosedRow,
  CourseListGroup,
} from '@/components/patient/course/CourseListCards';
import { BookTreatmentCourseSlideOver } from '@/components/appointments/BookTreatmentCourseSlideOver';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import {
  useAddTreatmentCourseStep,
  useCancelTreatmentCourse,
  useCancelTreatmentCourseStep,
  useRemoveTreatmentCourseStep,
  useTreatmentCourseStaffView,
  useTreatmentCoursesPatientList,
} from '@/hooks/useTreatmentCourses';
import { useResendQuestionnaire, useSendQuestionnaire } from '@/hooks/useClinioForms';
import { useServicesList } from '@/hooks/useServices';
import {
  readTreatmentCoursePatientParams,
  writeTreatmentCoursePatientParams,
} from '@/lib/treatmentCoursePatientParams';
import { sendStaffAppointmentEmail } from '@/lib/staffAppointmentConfirmation';
import { defaultSendCourseCancellationEmail, isZzTestPatient } from '@/lib/zzTestPatient';
import type { StaffCourseStep } from '@/lib/treatmentCourseStaffView';
import { Select } from '@/components/ui/Select';
import { patientJournalWritePath } from '@/lib/journalWritePath';
import { FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export function TreatmentCourseTab({
  clinicId,
  patientId,
  canEdit,
  patientName,
  currentUserId,
}: {
  clinicId: string;
  patientId: string;
  canEdit: boolean;
  patientName?: string | null;
  currentUserId?: string | null;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = readTreatmentCoursePatientParams(searchParams);
  const {
    data: courses,
    isLoading,
    error,
    refetch,
  } = useTreatmentCoursesPatientList(clinicId, patientId);
  const staffView = useTreatmentCourseStaffView(clinicId, view.courseId);
  const addStep = useAddTreatmentCourseStep(clinicId);
  const cancelStep = useCancelTreatmentCourseStep(clinicId);
  const removeStep = useRemoveTreatmentCourseStep(clinicId);
  const cancelCourse = useCancelTreatmentCourse(clinicId);
  const sendQuestionnaire = useSendQuestionnaire();
  const resendQuestionnaire = useResendQuestionnaire();
  const { data: services } = useServicesList(clinicId, { includeInactive: true });

  const [bookOpen, setBookOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addServiceId, setAddServiceId] = useState('');
  const [cancelTarget, setCancelTarget] = useState<StaffCourseStep | null>(null);
  const [removeTarget, setRemoveTarget] = useState<StaffCourseStep | null>(null);
  const [cancelCourseOpen, setCancelCourseOpen] = useState(false);
  const [sendCourseEmail, setSendCourseEmail] = useState(() =>
    defaultSendCourseCancellationEmail(patientName),
  );

  const sorted = useMemo(
    () => [...(courses ?? [])].sort((a, b) => a.template_name.localeCompare(b.template_name, 'da')),
    [courses],
  );
  const active = sorted.filter((course) => course.status === 'active');
  const closed = sorted.filter((course) => course.status !== 'active');
  const zzTest = isZzTestPatient(patientName);

  function setView(next: {
    courseId?: string | null;
    stepId?: string | null;
    edit?: boolean;
    journal?: boolean;
  }) {
    setSearchParams(writeTreatmentCoursePatientParams(searchParams, next));
  }

  function openCourse(id: string) {
    setView({ courseId: id, stepId: null, edit: false, journal: false });
  }

  function openStepSearch(courseId: string, stepId: string) {
    return writeTreatmentCoursePatientParams(searchParams, {
      courseId,
      stepId,
      edit: false,
      journal: false,
    }).toString();
  }

  async function remindQuestionnaire(step: StaffCourseStep, templateId: string) {
    const row = step.questionnaires.find((item) => item.forms_template_id === templateId);
    if (!row) return;
    try {
      if (row.assignment_id && (row.assignment_status === 'sent' || row.assignment_status === 'reminded')) {
        await resendQuestionnaire.mutateAsync({
          assignment_id: row.assignment_id,
          patient_id: patientId,
        });
        toast.success(t('treatment_courses.workspace_sent_now'));
        return;
      }
      const result = await sendQuestionnaire.mutateAsync({
        patient_id: patientId,
        template_id: templateId,
        metadata: {
          appointment_id: step.appointment_id,
          treatment_course_step_id: step.id,
          job_id: row.job_id,
          treatment_course_step_automation_id: row.automation_id,
        },
      });
      if (result.already_in_progress || result.already_sent) {
        toast(t('treatment_courses.workspace_send_already'));
        return;
      }
      toast.success(t('treatment_courses.workspace_sent_now'));
    } catch (err) {
      console.error(err);
      toast.error(t('treatment_courses.patient_toast_error'));
    }
  }

  if (view.courseId && view.edit) {
    return (
      <BookedTreatmentCourseEditor
        clinicId={clinicId}
        courseId={view.courseId}
        canEdit={canEdit}
        patientName={patientName}
        initialStepId={view.stepId}
        onBack={() =>
          setView({
            courseId: view.courseId,
            stepId: view.stepId,
            edit: false,
            journal: false,
          })
        }
        onOpenList={() => setView({ courseId: null, stepId: null, edit: false, journal: false })}
        onOpenCourse={() =>
          setView({ courseId: view.courseId, stepId: null, edit: false, journal: false })
        }
        onOpenStep={(id) => setView({ courseId: view.courseId, stepId: id, edit: false, journal: false })}
      />
    );
  }

  if (view.courseId && view.stepId && staffView.data) {
    return (
      <>
        <TreatmentCourseStepWorkspace
          clinicId={clinicId}
          course={staffView.data}
          stepId={view.stepId}
          canEdit={canEdit}
          currentUserId={currentUserId}
          patientName={patientName ?? ''}
          startJournal={view.journal}
          onOpenStep={(id) =>
            setView({ courseId: view.courseId, stepId: id, edit: false, journal: false })
          }
          onBack={() =>
            setView({ courseId: view.courseId, stepId: null, edit: false, journal: false })
          }
          onOpenList={() => setView({ courseId: null, stepId: null, edit: false, journal: false })}
          onEditStep={(id) =>
            setView({ courseId: view.courseId, stepId: id, edit: true, journal: false })
          }
          onCancelStep={setCancelTarget}
          onRemoveStep={setRemoveTarget}
        />
        {courseActionModals()}
      </>
    );
  }

  if (view.courseId) {
    if (staffView.isLoading) {
      return (
        <p className={cn(TYPE.small, 'text-ds-muted')}>{t('treatment_courses.patient_loading')}</p>
      );
    }
    if (staffView.error || !staffView.data) {
      return (
        <Banner
          tone="error"
          title={t('treatment_courses.patient_load_error')}
          action={
            <Button type="button" variant="secondary" onClick={() => void staffView.refetch()}>
              {t('app.retry')}
            </Button>
          }
        />
      );
    }
    return (
      <>
        <TreatmentCourseOverview
          course={staffView.data}
          canEdit={canEdit}
          onBack={() => setView({ courseId: null, stepId: null, edit: false, journal: false })}
          onOpenStep={(id) =>
            setView({ courseId: view.courseId, stepId: id, edit: false, journal: false })
          }
          onEditCourse={(id) =>
            setView({ courseId: view.courseId, stepId: id ?? null, edit: true, journal: false })
          }
          onCancelCourse={() => {
            setSendCourseEmail(defaultSendCourseCancellationEmail(patientName));
            setCancelCourseOpen(true);
          }}
          onAddStep={() => setAddOpen(true)}
          onCancelStep={setCancelTarget}
          onRemoveStep={setRemoveTarget}
          onWriteJournal={(id) => {
            const step = staffView.data?.steps.find((row) => row.id === id);
            navigate(
              patientJournalWritePath(patientId, {
                courseId: view.courseId,
                stepId: id,
                appointmentId: step?.appointment_id,
                source: 'step',
                from: `${location.pathname}${location.search}`,
              }),
            );
          }}
          onRemindQuestionnaire={remindQuestionnaire}
        />
        {courseActionModals()}
      </>
    );
  }

  function courseActionModals() {
    const course = staffView.data;
    return (
      <>
        <Modal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          title={t('treatment_courses.add_step')}
        >
          <label className="block">
            <span className={FORM_LABEL_TYPE}>{t('treatment_courses.field_service')}</span>
            <Select
              className="mt-1"
              value={addServiceId}
              onChange={(event) => setAddServiceId(event.target.value)}
            >
              <option value="">{t('treatment_courses.service_placeholder')}</option>
              {(services ?? [])
                .filter((service) => !service.is_blocker)
                .map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
            </Select>
          </label>
          <div className="mt-s4 flex justify-end gap-s2">
            <Button type="button" variant="secondary" onClick={() => setAddOpen(false)}>
              {t('treatment_courses.patient_close')}
            </Button>
            <Button
              type="button"
              disabled={!addServiceId || !course || addStep.isPending}
              onClick={async () => {
                if (!course) return;
                try {
                  await addStep.mutateAsync({
                    courseId: course.id,
                    payload: { service_id: addServiceId },
                  });
                  setAddOpen(false);
                  setAddServiceId('');
                  toast.success(t('treatment_courses.patient_toast_step_added'));
                } catch (err) {
                  console.error(err);
                  toast.error(t('treatment_courses.patient_toast_error'));
                }
              }}
            >
              {t('treatment_courses.add_step')}
            </Button>
          </div>
        </Modal>

        <ConfirmActionModal
          open={Boolean(cancelTarget)}
          title={t('treatment_courses.patient_cancel_step')}
          description={t('treatment_courses.patient_cancel_step_hint')}
          confirmLabel={t('treatment_courses.patient_cancel_step')}
          onClose={() => setCancelTarget(null)}
          onConfirm={async () => {
            if (!cancelTarget) return;
            try {
              await cancelStep.mutateAsync({ stepId: cancelTarget.id });
              toast.success(t('treatment_courses.patient_toast_step_cancelled'));
            } catch (err) {
              console.error(err);
              toast.error(t('treatment_courses.patient_toast_error'));
            } finally {
              setCancelTarget(null);
            }
          }}
        />

        <ConfirmActionModal
          open={Boolean(removeTarget)}
          title={t('treatment_courses.remove_step')}
          description={t('treatment_courses.patient_remove_step_hint')}
          confirmLabel={t('treatment_courses.remove_step')}
          onClose={() => setRemoveTarget(null)}
          onConfirm={async () => {
            if (!removeTarget) return;
            try {
              await removeStep.mutateAsync({ stepId: removeTarget.id });
              toast.success(t('treatment_courses.patient_toast_step_removed'));
              if (view.stepId === removeTarget.id) {
                setView({ courseId: view.courseId, stepId: null, edit: false, journal: false });
              }
            } catch (err) {
              console.error(err);
              toast.error(t('treatment_courses.patient_toast_error'));
            } finally {
              setRemoveTarget(null);
            }
          }}
        />

        {course ? (
          <CancelTreatmentCourseModal
            open={cancelCourseOpen}
            onClose={() => setCancelCourseOpen(false)}
            pending={cancelCourse.isPending}
            sendEmail={sendCourseEmail}
            onSendEmailChange={setSendCourseEmail}
            zzTestPatient={zzTest}
            onConfirm={() => {
              void (async () => {
                try {
                  const result = await cancelCourse.mutateAsync({ courseId: course.id });
                  if (sendCourseEmail) {
                    for (const appointmentId of result.cancelledAppointmentIds) {
                      sendStaffAppointmentEmail(appointmentId, 'cancelled');
                    }
                  }
                  toast.success(t('treatment_courses.patient_toast_course_cancelled'));
                  setCancelCourseOpen(false);
                } catch (err) {
                  console.error(err);
                  toast.error(t('treatment_courses.patient_toast_error'));
                }
              })();
            }}
          />
        ) : null}
      </>
    );
  }

  if (isLoading || error) {
    return (
      <PatientTabFrame
        className="fl"
        title={t('patients.tabs.treatment_courses')}
        count={sorted.length}
        loading={isLoading}
        error={error ? t('treatment_courses.patient_load_error') : null}
        onRetry={() => {
          void refetch();
        }}
        action={
          canEdit ? (
            <Button type="button" onClick={() => setBookOpen(true)}>
              <Plus className="i sm" aria-hidden />
              {t('treatment_courses.new_course')}
            </Button>
          ) : null
        }
      />
    );
  }

  return (
    <>
      <CourseTabPanel testId="treatment-course-list">
        <div className="tf-h">
          <h2 id="h-fl">{t('patients.tabs.treatment_courses')}</h2>
          <CourseCount value={sorted.length} />
          {canEdit ? (
            <div className="r">
              <Button type="button" onClick={() => setBookOpen(true)}>
                <Plus className="i sm" aria-hidden />
                {t('treatment_courses.new_course')}
              </Button>
            </div>
          ) : null}
        </div>
        {sorted.length === 0 ? (
          <div className="tf-empty">
            <span className="ic">
              <Layers className="i" aria-hidden />
            </span>
            <p className="e1">{t('treatment_courses.patient_empty')}</p>
            <p className="e2">{t('treatment_courses.patient_empty_hint')}</p>
          </div>
        ) : (
          <div className="tf-b">
            {active.length > 0 ? (
              <CourseListGroup title={t('treatment_courses.list_active')} count={active.length}>
                <div className="space-y-s3">
                  {active.map((course) => (
                    <CourseListActiveCard
                      key={course.id}
                      course={course}
                      onOpen={() => openCourse(course.id)}
                      openStepSearch={(stepId) => openStepSearch(course.id, stepId)}
                    />
                  ))}
                </div>
              </CourseListGroup>
            ) : null}
            {closed.length > 0 ? (
              <CourseListGroup title={t('treatment_courses.list_completed')} count={closed.length}>
                <div className="space-y-s2">
                  {closed.map((course) => (
                    <CourseListClosedRow
                      key={course.id}
                      course={course}
                      onOpen={() => openCourse(course.id)}
                    />
                  ))}
                </div>
              </CourseListGroup>
            ) : null}
          </div>
        )}
      </CourseTabPanel>
      {bookOpen ? (
        <BookTreatmentCourseSlideOver
          clinicId={clinicId}
          initialPatientId={patientId}
          onClose={() => setBookOpen(false)}
          onCreated={(created) => openCourse(created.id)}
        />
      ) : null}
    </>
  );
}
